import React, { createContext, useContext, useState } from 'react'

import { usePurchaseOrders } from '@/api/usePurchaseOrders'
import { PurchaseOrderFilters } from '@/interfaces/purchaseOrder'

interface TableContextType {
  // Data
  purchaseOrderData: any
  isLoading: boolean
  error: any

  // Pagination
  currentPage: number
  setCurrentPage: (page: number) => void
  pageSize: number
  setPageSize: (size: number) => void
  goToNextPage: () => void
  goToPreviousPage: () => void

  // Filters
  filters: PurchaseOrderFilters
  setFilters: (filters: PurchaseOrderFilters) => void
  searchTerm: string
  setSearchTerm: (term: string) => void

  // Actions
  refetch: () => void

  // Additional properties for enhanced header functionality
  isFetching?: boolean
  skeletonRowCount?: number
}

const TableContext = createContext<TableContextType | undefined>(undefined)

export const useTableContext = (): TableContextType => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error('useTableContext must be used within a TableContextProvider')
  }
  return context
}

interface TableContextProviderProps {
  children: React.ReactNode
}

export const TableContextProvider: React.FC<TableContextProviderProps> = ({
  children,
}) => {
  // State management
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filters, setFilters] = useState<PurchaseOrderFilters>({})
  const [searchTerm, setSearchTerm] = useState('')

  // Query parameters
  const queryParams = {
    page: currentPage,
    limit: pageSize,
    search: searchTerm || undefined,
    ...filters,
  }

  // Data fetching - TEMPORALMENTE DESHABILITADO para desarrollo
  // const {
  //   data: purchaseOrderData,
  //   isLoading,
  //   error,
  //   refetch,
  // } = usePurchaseOrders(queryParams)
  
  // Mock data para desarrollo sin endpoints
  const purchaseOrderData = {
    data: [],
    total: 0,
    totalPages: 0,
    currentPage: 1
  }
  const isLoading = false
  const error = null
  const refetch = () => {
    console.log('Refetch llamado - endpoints no disponibles aún')
  }

  // Pagination handlers
  const goToNextPage = () => {
    if (purchaseOrderData && currentPage < purchaseOrderData.totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [filters, searchTerm])

  const value: TableContextType = {
    // Data
    purchaseOrderData,
    isLoading,
    error,

    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    goToNextPage,
    goToPreviousPage,

    // Filters
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,

    // Actions
    refetch,

    // Additional properties for enhanced header functionality
    isFetching: isLoading,
    skeletonRowCount: 10,
  }

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>
}
